import { useCallback } from 'react';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { log } from '@/logger/logger';
import { copyImage } from '@/utils/files';
import { useWorkflowStore } from '@/state/workflow.state';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { isMediaAsset } from './utils';
import type { UploadedAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

export interface ImageActions {
  handleDownload: (index: number) => Promise<void>;
  handleDownloadAll: () => Promise<void>;
  handleCopyImage: (index: number) => Promise<void>;
}

interface ImageActionsOptions {
  handleContextMenuClose: () => void;
  images: (UploadedAsset | FileKind)[];
  nodeName: string;
}

const logger = log.getLogger('ImageActions');

export const useImageActions = ({ handleContextMenuClose, images, nodeName }: ImageActionsOptions): ImageActions => {
  const { track } = useAnalytics();
  const recipeName = useWorkflowStore((state) => state.recipe.name);
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();
  const handleDownload = useCallback(
    async (index: number) => {
      track('node_image_download_clicked', {}, TrackTypeEnum.BI);
      const file = images[index];
      if (index !== null && isMediaAsset(file)) {
        await downloadAssetFromNode({
          url: file.url,
          fileType: file.type,
          nodeName,
          prefix: recipeName,
        });
      }
      handleContextMenuClose();
    },
    [handleContextMenuClose, images, nodeName, track, recipeName, downloadAssetFromNode],
  );

  const handleDownloadAll = useCallback(async () => {
    track('node_image_download_all_clicked', {}, TrackTypeEnum.BI);
    await downloadAssetsFromNodeWithProgress(images.filter(isMediaAsset), nodeName, recipeName, undefined);
    handleContextMenuClose();
  }, [handleContextMenuClose, images, nodeName, track, recipeName, downloadAssetsFromNodeWithProgress]);

  const handleCopyImage = useCallback(
    async (index: number) => {
      track('node_image_copy_clicked', {}, TrackTypeEnum.BI);
      try {
        const file = images[index];
        if (index !== null && file?.type === 'image' && 'url' in file) {
          await copyImage(file);
        }
      } catch (error) {
        logger.error('Failed to copy image:', error as Error);
      }
      handleContextMenuClose();
    },
    [handleContextMenuClose, images, track],
  );

  return {
    handleDownload,
    handleDownloadAll,
    handleCopyImage,
  };
};
